// MessageDraftDialog — Session 7 PR3.
//
// Two-stage flow inside one dialog:
//   1. Pick kind + channel + optional focus + optional linked application.
//      Hit Generate → Sonnet returns a body via /api/contacts/:id/messages/draft.
//   2. Review/edit the body → Mark sent → POST /api/contacts/:id/messages/sent.
//      That single call handles the artifact insert + Vectorize embed +
//      contact.last_contacted_at bump + agent timeline event (when linked).

import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import { Copy, Loader2 } from "lucide-react";
import {
  MESSAGE_KINDS,
  type Contact,
  type DraftMessageResponse,
  type MessageKind,
} from "@resume-cloudflare/shared";
import { Dialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Textarea } from "../ui/Textarea";
import { Select } from "../ui/Select";
import { Pill } from "../ui/Pill";
import { useDraftMessage, useMarkMessageSent } from "../../lib/contacts";
import { useApplications } from "../../lib/applications";
import { toast } from "../ui/Toaster";

const KIND_LABELS: Record<MessageKind, string> = {
  cold_intro_for_referral: "Cold intro for referral",
  post_application_followup: "Post-application follow-up",
  post_interview_thankyou: "Post-interview thank-you",
  recruiter_response: "Recruiter response",
  reconnect_and_ask: "Reconnect and ask",
};

const NO_APP = "__none__";

export function MessageDraftDialog({
  contact,
  trigger,
}: {
  contact: Contact;
  trigger: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<MessageKind>("cold_intro_for_referral");
  const [channel, setChannel] = useState<"linkedin" | "email">("email");
  const [applicationId, setApplicationId] = useState<string>(NO_APP);
  const [focus, setFocus] = useState("");
  const [body, setBody] = useState("");
  const [edited, setEdited] = useState(false);
  const [draftMeta, setDraftMeta] = useState<DraftMessageResponse | null>(null);

  const draft = useDraftMessage(contact.id);
  const markSent = useMarkMessageSent(contact.id);
  // Always run; tanstack-query dedupes against the kanban's parallel fetch.
  const apps = useApplications();

  useEffect(() => {
    if (!open) {
      setBody("");
      setDraftMeta(null);
      setFocus("");
      setEdited(false);
    }
  }, [open]);

  function generate() {
    draft.mutate(
      {
        kind,
        channel,
        application_id: applicationId === NO_APP ? null : applicationId,
        focus: focus.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          setBody(data.body);
          setDraftMeta(data);
          setEdited(false);
        },
      },
    );
  }

  function copy() {
    navigator.clipboard
      .writeText(body)
      .then(() => toast.success("Copied to clipboard."))
      .catch(() => toast.error("Copy failed."));
  }

  function send() {
    if (!body.trim()) return;
    markSent.mutate(
      {
        kind,
        body: body.trim(),
        application_id: applicationId === NO_APP ? null : applicationId,
        channel,
        edited,
      },
      { onSuccess: () => setOpen(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Content
        className="max-w-2xl w-[min(92vw,42rem)]"
        title={`Draft message — ${contact.full_name}`}
        description="Generates with Sonnet from your career corpus + past messages of this kind. Edit before marking sent."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={copy}
              disabled={!body}
            >
              <Copy size={14} /> Copy
            </Button>
            <Button
              onClick={send}
              disabled={!body.trim() || markSent.isPending}
            >
              {markSent.isPending ? "Marking…" : "Mark sent"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-neutral-500">
                Kind
              </label>
              <Select value={kind} onChange={(e) => setKind(e.target.value as MessageKind)}>
                {MESSAGE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABELS[k]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-neutral-500">
                Channel
              </label>
              <Select
                value={channel}
                onChange={(e) => setChannel(e.target.value as "linkedin" | "email")}
              >
                <option value="email">Email</option>
                <option value="linkedin">LinkedIn DM</option>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              Linked application (optional)
            </label>
            <Select
              value={applicationId}
              onChange={(e) => setApplicationId(e.target.value)}
            >
              <option value={NO_APP}>None — contact-only</option>
              {(apps.data?.applications ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {(a.company_name ?? "Other")} — {a.role}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              Focus / context (optional)
            </label>
            <Textarea
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              rows={2}
              placeholder="Recruiter said the team is hiring fast. Mention the platform-engineering thread."
            />
          </div>

          <div className="flex items-center justify-between">
            <Button onClick={generate} disabled={draft.isPending}>
              {draft.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Generating…
                </>
              ) : draftMeta ? (
                "Regenerate"
              ) : (
                "Generate draft"
              )}
            </Button>
            {draftMeta ? (
              <div className="flex items-center gap-2 text-[11px] text-neutral-500">
                <Pill tone={draftMeta.used_template_override ? "info" : "neutral"}>
                  {draftMeta.used_template_override ? "Custom prompt" : "Default prompt"}
                </Pill>
                <span>
                  {draftMeta.voice_reference_ids.length} past msg ·{" "}
                  {draftMeta.career_unit_ids.length} corpus units
                </span>
              </div>
            ) : null}
          </div>

          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              Message body
            </label>
            <Textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setEdited(true);
              }}
              rows={12}
              placeholder="Generated message will appear here. Edit freely; your edits feed into next time."
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
